import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import useAuthStore from '../store/useAuthStore';

/**
 * Hook that bridges react-oidc-context with our Zustand auth store
 * This keeps the auth store synchronized with the Cognito authentication state
 */
export const useAuthSync = () => {
  const auth = useAuth();
  const { 
    setAuthContext, 
    clearAuth,
    user,
    isLoading,
    error,
    fetchUserProfile,
    refreshUserProfile,
    updateUserProfile,
    getUserDisplayName,
    getUsername,
    getUserEmail,
    getUserId,
    getOrganizationId,
    getUserRole,
    getAppRole,
    hasRole,
    hasPermission,
    isMedicalDataEnabled,
    clearError,
    isProfileComplete,
    getMissingProfileFields
  } = useAuthStore();

  // Sync auth context changes with the store
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      setAuthContext(auth);
    } else if (!auth.isAuthenticated) {
      clearAuth();
    }
  }, [auth, setAuthContext, clearAuth]);

  return {
    // Cognito auth state (from react-oidc-context)
    cognitoAuth: auth,
    
    // User profile data (from our store)
    user,
    isLoading,
    error,
    
    // Authentication status
    isAuthenticated: auth.isAuthenticated,
    
    // User actions
    fetchUserProfile,
    refreshUserProfile,
    updateUserProfile,
    clearError,
    
    // User getters
    getUserDisplayName,
    getUsername,
    getUserEmail,
    getUserId,
    getOrganizationId,
    getUserRole,
    getAppRole,
    hasRole,
    hasPermission,
    isMedicalDataEnabled,
    isProfileComplete,
    getMissingProfileFields,
    
    // Auth actions
    signIn: () => auth.signinRedirect(),
    signOut: () => auth.signoutRedirect(),
    signOutLocal: () => {
      clearAuth();
      auth.removeUser();
    }
  };
};
